import moment from 'moment';
import {useLayoutEffect, useRef, useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Datapoint} from '@epic-visualizations/charts';

import {RootState} from '../../../../../index';
import {AUTO_UPDATED_TIMESCALES, HOURLY_UPDATE_INTERVAL} from '../constants';
import {getEntryTime, formatVersion} from '../utils';
import {getPerformanceState} from '../../../../../selectors/fortnite-game/performanceSelector';
import {PerformanceState} from '../../../../../reducers/fortnite-game/performance.reducer';
import performanceActions from '../../../../../actions/fortnite-game/performance.actions';

import {DateMoment, TimeScale, Platform, CombinedPlatform} from '../../../../../types';
import {Metric} from '../../../../../../shared/types/FortniteGames';
import {getHpm, getMvp, getPlatformStats} from './utils';

export interface UsePerformanceDataParams {
    creativeIslandCode: string;
    platform: CombinedPlatform;
    metric: Metric;
    timeScale: TimeScale;
    startDate: DateMoment;
    endDate: DateMoment;
}

export interface UsePerformanceState extends Omit<PerformanceState, 'data'> {
    data: Datapoint[];
    reload: (silent: boolean, overwriteCache: boolean) => void;
}

const usePerformanceData = ({
    creativeIslandCode,
    platform,
    metric,
    timeScale,
    startDate,
    endDate
}: UsePerformanceDataParams): UsePerformanceState => {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);

    const {data, versions = [], wau, loading, lastFetch, error} = useSelector<
        RootState,
        PerformanceState
    >(getPerformanceState, shallowEqual);

    const formattedData = useMemo(
        () =>
            data && data[timeScale]
                ? data[timeScale].map(({time, platforms}) => ({
                      [CombinedPlatform.CLIENT_OVERALL]: getPlatformStats(
                          Object.values(Platform),
                          platforms
                      ),
                      [CombinedPlatform.CLIENT_CONSOLE]: getPlatformStats(
                          [Platform.PLAYSTATION_4, Platform.XBOX_ONE],
                          platforms
                      ),
                      [CombinedPlatform.CLIENT_CONSOLE_NG]: getPlatformStats(
                          [Platform.PLAYSTATION_5, Platform.XBOX_SERIES_X],
                          platforms
                      ),
                      [CombinedPlatform.CLIENT_CONSOLE_MOBILE]: platforms.Switch,
                      [CombinedPlatform.CLIENT_PC]: platforms.Windows,
                      [CombinedPlatform.CLIENT_MOBILE]: platforms.Android,
                      time: getEntryTime(time, timeScale)
                  }))
                : [],
        [data, timeScale]
    );

    const selectedData = useMemo(
        () =>
            formattedData.map(({time, ...rest}) => {
                const {ef, fc, sec, th} = rest[platform] || {};

                return {
                    [platform]:
                        metric === Metric.HITCHES_RATE ? getHpm(sec, th) : getMvp(sec, fc, ef),
                    time
                };
            }),
        [formattedData, platform, metric]
    );

    const xAxisTicks = formattedData.map(({time}) => getEntryTime(time, timeScale));

    // If there is no data when version was activated, versions is displaced to the nearest date with data.
    // Otherwise version would not be visible
    const formattedVersions = versions.map(version =>
        moment(version.activated).isBetween(startDate, endDate)
            ? formatVersion(version, xAxisTicks, timeScale)
            : version
    );

    /*
     * @param silent - Don't toggle loading state
     * @param overwriteCache - Overwrite cached data
     * */
    const fetchData = (silent = false) => {
        dispatch(
            performanceActions.getPerformance({
                island: creativeIslandCode,
                fromTs: startDate ? startDate.valueOf() : undefined,
                toTs: endDate ? endDate.valueOf() : undefined,
                timescale: timeScale,
                silent
            })
        );
    };

    useLayoutEffect(() => {
        if (creativeIslandCode) {
            fetchData(false);

            if (AUTO_UPDATED_TIMESCALES.includes(timeScale)) {
                intervalRef.current = setInterval(() => {
                    fetchData(true);
                }, HOURLY_UPDATE_INTERVAL);

                return () => {
                    clearInterval(intervalRef.current);
                };
            }
        }
    }, [creativeIslandCode, timeScale, startDate, endDate]);

    return {
        data: selectedData,
        versions: formattedVersions,
        wau,
        loading,
        lastFetch,
        error,
        reload: () => fetchData(false)
    };
};

export default usePerformanceData;
